import { z } from "zod/v4";

const STORAGE_KEY = "wayflyer:webmcp:contact-prefill";

const CONTACT_FIELD_NAMES = new Set([
  "firstname",
  "lastname",
  "email",
  "phone",
  "0-2/domain",
  "0-2/ib_country_incorporated_reported",
  "0-2/ib_monthly_revenue_usd_reported",
  "0-2/source_of_revenue",
]);

type ContactPrefill = Record<string, string>;
type PrefillListener = (prefill: ContactPrefill) => void;

interface FieldDefinition {
  _type?: string;
  name?: string;
  type?: string;
  fields?: FieldDefinition[];
  options?: Array<{ label?: string; value?: string }>;
}

let pendingPrefill: ContactPrefill | null = null;
const listeners = new Set<PrefillListener>();

function schemaForField(field: FieldDefinition): z.ZodType<string> {
  if (field._type === "dropdown") {
    const values = (field.options ?? [])
      .map((option) => option.value ?? option.label)
      .filter((value): value is string => Boolean(value));
    return z.string().refine((value) => values.includes(value), {
      message: "Value is not an option for this form field",
    });
  }

  switch (field.type) {
    case "email":
      return z.string().email();
    case "tel":
      return z
        .string()
        .regex(/^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/);
    case "url":
      return z.string().url();
    default:
      return z.string().min(1);
  }
}

function flattenFields(fields: FieldDefinition[]): FieldDefinition[] {
  return fields.flatMap((field) =>
    field._type === "fieldset" ? flattenFields(field.fields ?? []) : [field]
  );
}

export function createContactPrefillSchema(fields: unknown[]) {
  const shape: Record<string, z.ZodType<string>> = {};

  for (const field of flattenFields(fields as FieldDefinition[])) {
    if (
      field.name &&
      CONTACT_FIELD_NAMES.has(field.name) &&
      (field._type === "textField" || field._type === "dropdown")
    ) {
      shape[field.name] = schemaForField(field).optional() as z.ZodType<string>;
    }
  }

  return z.object(shape).strict();
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function writeContactPrefill(prefill: ContactPrefill): void {
  if (listeners.size > 0) {
    pendingPrefill = null;
    try {
      storage()?.removeItem(STORAGE_KEY);
    } catch {
      // Best-effort cleanup in browsers that block storage.
    }
    for (const listener of listeners) listener(prefill);
    return;
  }

  pendingPrefill = prefill;
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(prefill));
  } catch {
    // Storage is only a navigation fallback; the in-memory handoff still works.
  }
}

export function readContactPrefill(): ContactPrefill | null {
  let value = pendingPrefill;
  pendingPrefill = null;

  const sessionStorage = storage();
  if (!value && sessionStorage) {
    try {
      const serialized = sessionStorage.getItem(STORAGE_KEY);
      if (serialized) value = JSON.parse(serialized) as ContactPrefill;
    } catch {
      value = null;
    }
  }
  try {
    sessionStorage?.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort cleanup in browsers that block storage.
  }

  return value;
}

export function subscribeContactPrefill(listener: PrefillListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
